import React, { useState } from 'react';
import { Trans } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { SvgBarco, SvgLogo } from '@actual-app/components/icons/logo';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { useGlobalPref } from '#hooks/useGlobalPref';
import { useReducedMotion } from '#hooks/useReducedMotion';
import { pushModal } from '#modals/modalsSlice';
import { appName } from '#presupuesto';
import { useDispatch } from '#redux';

import { Lema } from './Lema';
import { Astillero } from './puerto/Astillero';
import { Paso0Bienvenida } from './puerto/Paso0Bienvenida';
import { PORTADAS } from './puerto/portada';
import { useBotarBarco } from './puerto/useBotarBarco';

const fadeInUp = keyframes({
  from: { opacity: 0, transform: 'translateY(12px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

function entrance(delay: number) {
  return css({
    animationName: fadeInUp,
    animationDuration: '500ms',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'backwards',
    animationDelay: `${delay}ms`,
  });
}

/**
 * Qué se ve encima de la bienvenida: nada, el paso 0 de la travesía (pedir
 * el nombre), el Astillero de la travesía (paso 1) o el Astillero de siempre.
 */
type FaseBienvenida = 'inicio' | 'paso0' | 'astillero-travesia' | 'astillero';

function WelcomeScreenPresupuesto() {
  const dispatch = useDispatch();
  const [fase, setFase] = useState<FaseBienvenida>('inicio');
  const [nombrePerfil, setNombrePerfil] = useGlobalPref('nombrePerfil');
  const [, setTratoPerfil] = useGlobalPref('tratoPerfil');
  const { onBotar } = useBotarBarco();
  const { isNarrowWidth } = useResponsive();
  const isReducedMotion = useReducedMotion();

  const entranceClass = (delay: number) =>
    isReducedMotion ? undefined : entrance(delay);

  const buttonStyle = {
    fontSize: 15,
    padding: '12px 15px',
    flexShrink: 0,
    ...(isNarrowWidth && { minHeight: styles.mobileMinHeight }),
  };

  return (
    <View
      style={{
        alignItems: 'center',
        gap: 25,
        width: '100%',
        maxWidth: 480,
        fontSize: 15,
        maxHeight: '100%',
        overflowY: 'auto',
        paddingTop: 20,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <View
        className={entranceClass(0)}
        style={{ alignItems: 'center', gap: 10, flexShrink: 0 }}
      >
        <SvgLogo width={112} height={112} aria-label={appName} />
        <Text style={{ ...styles.veryLargeText, textAlign: 'center' }}>
          {appName}
        </Text>
        <Text
          style={{
            color: theme.pageTextPositive,
            textAlign: 'center',
            fontSize: 17,
            fontWeight: 600,
            marginTop: -4,
          }}
        >
          <Trans>Budget your course</Trans>
        </Text>
        <Lema
          data-testid="lema-bienvenida"
          style={{ textAlign: 'center', marginTop: 4 }}
        />
        <SvgBarco
          width={96}
          height={32}
          style={{ color: theme.pageTextSubdued, opacity: 0.7 }}
        />
      </View>

      <View
        className={entranceClass(150)}
        style={{ width: '100%', maxWidth: 400, gap: 10, flexShrink: 0 }}
      >
        <Button
          variant="primary"
          autoFocus={!isNarrowWidth}
          style={buttonStyle}
          onPress={() =>
            dispatch(pushModal({ modal: { name: 'import-ynab5' } }))
          }
        >
          <Trans>Import from YNAB</Trans>
        </Button>
        <Button
          style={buttonStyle}
          data-testid="empezar-de-cero"
          onPress={() => setFase(nombrePerfil ? 'astillero' : 'paso0')}
        >
          <Trans>Start from scratch</Trans>
        </Button>
      </View>
      {fase === 'paso0' && (
        <Paso0Bienvenida
          onContinuar={(nombre, trato) => {
            setNombrePerfil(nombre);
            if (trato) {
              setTratoPerfil(trato);
            }
            setFase('astillero-travesia');
          }}
          onSaltar={() => setFase('astillero')}
        />
      )}
      {(fase === 'astillero' || fase === 'astillero-travesia') && (
        <Astillero
          colorInicial={PORTADAS[0].id}
          onBotar={onBotar}
          onCerrar={() => setFase('inicio')}
          travesia={
            fase === 'astillero-travesia'
              ? { paso: 1, onSaltar: () => setFase('astillero') }
              : undefined
          }
        />
      )}
    </View>
  );
}

export function WelcomeScreen() {
  return <WelcomeScreenPresupuesto />;
}
